import { InAppBrowser, DefaultWebViewOptions, DefaultSystemBrowserOptions } from '@capacitor/inappbrowser';
import * as Sentry from '@sentry/react';

import { logInDev } from '@ocx/utils';
import { isNativePlatform } from './device.utils';

// TODO extend this util to support onClose and onLoad callbacks if needed in the future

export const openUrlInWindow = async (params: { url: string }) => {
  try {
    // For native platforms, use InAppBrowser to open the URL
    if (isNativePlatform()) {
      await InAppBrowser.openInWebView({
        url: params.url,
        options: DefaultWebViewOptions,
      });
      return;
    }

    // For web platform, open the URL in a new tab
    const webWindow = window.open(params.url, '_blank', 'noopener,noreferrer');
    if (!webWindow) {
      Sentry.addBreadcrumb({ message: 'Device Utils: Open URL in window: Failed to open popup window (Web)' });
    }
  } catch (e) {
    Sentry.addBreadcrumb({ message: 'Device Utils: Open URL in window: Failed to open Url in window' });
    logInDev(e);
  }
};

const defaultLoadingHtml = '<html><head><title>Loading...</title></head><body>Loading...</body></html>';
const closeWindow = (params: { webWindow: Window }) => {
  params.webWindow.close();
  window.focus();
};

export const openUrlInWindowAsync = async (params: {
  urlGetter: () => Promise<string | null>;
  loadingHtmlContent?: string;
}) => {
  try {
    const { urlGetter, loadingHtmlContent = defaultLoadingHtml } = params;

    // For native platforms, use InAppBrowser to open the URL
    if (isNativePlatform()) {
      const url = await urlGetter();
      if (!url) return;
      await InAppBrowser.openInSystemBrowser({
        url,
        options: DefaultSystemBrowserOptions,
      });
      return;
    }

    // Open a blank window immediately to avoid popup blockers
    const webWindow = window.open('about:blank', '_blank');
    if (!webWindow) {
      Sentry.addBreadcrumb({ message: 'Device Utils: Open URL in window async: Failed to open popup window (Web)' });
      return;
    }

    // Render loading content in empty window
    webWindow.document.open();
    webWindow.document.writeln(loadingHtmlContent);
    webWindow.document.close();

    // Get the URL asynchronously and close the window if URL is not available
    const url = await urlGetter();
    if (!url) {
      return closeWindow({ webWindow });
    }

    // Navigate to the URL, close the window if navigation fails
    try {
      webWindow.location.href = url;
    } catch (e) {
      closeWindow({ webWindow });
      // Re-throw the error to be caught by the outer catch
      throw e;
    }
  } catch (e) {
    Sentry.addBreadcrumb({ message: 'Device Utils: Open URL in window async: Failed to open URL' });
    logInDev(e);
  }
};
